// This keeps a list of files that are being uploaded, and puts them in a context.
// A function to add files is also included.

// The other component in this feature, file upload state dialog, shows that state.

import { PropsWithChildren, createContext } from "react";

import { FileUploadingDialog } from "./components/file-uploading-dialog";
import { FileUploadingOverwriteModal } from "./components/file-uploading-overwrite-modal";
import { useFileUploadingOverwriteModal } from "./hooks/use-file-uploading-overwrite-modal";
import { useFileUploadingProvider } from "./hooks/use-file-uploading-provider";
import {
  UploadFileState,
  UploadFilesArgs,
  UploadFilesWithOverwriteConfirmArgs,
} from "./types/upload-types";

interface IFileUploadingContext extends UploadFileState {
  uploadFiles: (fileArgs: UploadFilesArgs, onComplete?: () => void) => void;
  uploadFilesWithOverwriteConfirm: (
    filesArgs: UploadFilesWithOverwriteConfirmArgs,
    onComplete?: () => void
  ) => void;
  cancelUpload: (number: number) => void;
  showFileModal: boolean;
}

export const FileUploadingContext = createContext<IFileUploadingContext>({
  nextNumber: 1,
  filesBeingUploaded: [],
  uploadFiles: () => undefined,
  uploadFilesWithOverwriteConfirm: () => undefined,
  cancelUpload: () => undefined,
  showFileModal: false,
});

export function FileUploadingProvider({ children }: PropsWithChildren) {
  const { nextNumber, filesBeingUploaded, cancelUpload, uploadFiles, showFileModal } =
    useFileUploadingProvider();

  const { uploadFilesWithOverwriteConfirm, overwriteState, setOverwrites, startOverwritesUpload } =
    useFileUploadingOverwriteModal(uploadFiles);

  return (
    <FileUploadingContext.Provider
      value={{
        nextNumber,
        filesBeingUploaded,
        cancelUpload,
        uploadFiles,
        showFileModal,
        uploadFilesWithOverwriteConfirm,
      }}
    >
      {overwriteState && (
        <FileUploadingOverwriteModal
          setOverwrites={setOverwrites}
          startOverwritesUpload={startOverwritesUpload}
          overwriteState={overwriteState}
        />
      )}
      <FileUploadingDialog />
      {children}
    </FileUploadingContext.Provider>
  );
}
